import { BadgeCheck, CheckCircle2, HeartHandshake } from "lucide-react";
import type { CSSProperties } from "react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useActivateFree,
  useCheckout,
  usePayInfo,
} from "@/features/payments/hooks/usePayments";
import type { Offer } from "@/features/payments/payments.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

const OFFER_COPY = {
  free: {
    title: "Free membership",
    description: "Appear in the directory and access core community resources.",
    cta: "Continue free",
  },
  donation: {
    title: "Free + donation",
    description: "Free membership, plus a one-time tax-deductible donation to support AFELL administration costs. You choose the amount.",
    cta: "Donate & activate",
  },
  standard: {
    title: "Annual membership",
    description: "Full access to the resource library, including paid-member guides and templates.",
    cta: "Pay with Stripe",
  },
} as const;

const offerKey = (offer: Offer) =>
  offer.kind === "paid" ? offer.variant : offer.kind;

const whole = (cents: number) => (cents / 100).toFixed(0);
const exact = (cents: number) => (cents / 100).toFixed(2);

function PayPageSkeleton() {
  return (
    <div className="mt-6">
      <Skeleton className="h-7 w-72" />
      <Skeleton className="mt-2 h-4 w-96 max-w-full" />
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-9 w-24" />
            </CardContent>
            <CardFooter>
              <Skeleton className="h-8 w-full" />
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

/** Membership activation via emailed payment link. Free activates immediately;
 * paid and donation redirect to Stripe Checkout. */
export default function PayPage() {
  const { token } = useParams();
  const { data: info, isPending, error } = usePayInfo(token);
  const activateFree = useActivateFree(token);
  const checkout = useCheckout(token);
  // Which card was clicked — the mutations only expose a global isPending.
  const [busy, setBusy] = useState<string | null>(null);
  // Cart extras. They only apply to a paid membership, so choosing free or
  // the standalone donation ignores them rather than silently charging.
  // Sponsoring is buying more than one membership, so it is a count rather
  // than a checkbox. Seeded from the offer, which starts at 2 for someone who
  // registered as a sponsor.
  const [quantity, setQuantity] = useState<number | null>(null);
  const [donate, setDonate] = useState(false);

  function choose(offer: Offer) {
    const key = offerKey(offer);
    setBusy(key);
    const onError = () => setBusy(null);
    if (offer.kind === "free") {
      activateFree.mutate(undefined, { onError });
    } else if (offer.kind === "donation") {
      // useCheckout redirects to Stripe on success, so busy stays set.
      checkout.mutate({ kind: "donation" }, { onError });
    } else {
      checkout.mutate(
        {
          kind: "membership",
          variant: offer.variant,
          quantity: quantity ?? offer.defaultQuantity,
          donation: donate,
        },
        { onError }
      );
    }
  }

  const failure = activateFree.error || checkout.error;
  // Extras attach to a membership, so there is nothing to add without one.
  const hasPaidOffer = (info?.offers ?? []).some((o) => o.kind === "paid");

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-16">
      <Link to="/" className="text-muted-foreground text-sm hover:underline">
        ← AFELL
      </Link>

      {activateFree.isSuccess ? (
        <Card className="animate-page-enter mt-6">
          <CardHeader>
            <CheckCircle2 className="animate-item-enter mb-2 size-8 text-green-600" />
            <CardTitle>Membership activated</CardTitle>
            <CardDescription>
              Your free AFELL membership is active and you now appear in the
              member directory. Next, sign in with the same email address you
              applied with. That is what makes the account.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button asChild>
              <Link to="/sign-in">Sign in</Link>
            </Button>
          </CardFooter>
        </Card>
      ) : isPending ? (
        <PayPageSkeleton />
      ) : error || !info ? (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>This link is no longer valid</CardTitle>
            <CardDescription>
              It may have already been used to activate a membership. If you
              think this is a mistake, contact an AFELL administrator.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          <div className="animate-page-enter mt-6">
            <h1 className="text-2xl">Complete your membership</h1>
            <p className="text-muted-foreground mt-1">
              Welcome, {info.name}! Choose a membership to activate your
              account.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {info.testMode && (
                <Badge variant="outline">
                  Test mode: use card 4242 4242 4242 4242
                </Badge>
              )}
              {info.requestedType && (
                <Badge variant="secondary">
                  You selected: {info.requestedType}
                </Badge>
              )}
            </div>
          </div>

          <div
            className={cn(
              "mt-6 grid gap-4 sm:grid-cols-2",
              info.offers.length > 2 && "lg:grid-cols-3"
            )}
          >
            {info.offers.map((offer, i) => {
              const key = offerKey(offer);
              const copy = OFFER_COPY[key];
              return (
                <Card
                  key={key}
                  className={cn(
                    "animate-item-enter flex flex-col",
                    offer.recommended && "border-primary"
                  )}
                  style={{ "--stagger": i + 1 } as CSSProperties}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {copy.title}
                      {offer.kind === "donation" ? (
                        <HeartHandshake className="size-4" />
                      ) : (
                        offer.kind === "paid" && <BadgeCheck className="size-4" />
                      )}
                    </CardTitle>
                    {offer.recommended && (
                      <Badge className="w-fit">Your selection</Badge>
                    )}
                    <CardDescription>{copy.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto">
                    {offer.kind === "free" ? (
                      <p className="text-3xl font-semibold">$0</p>
                    ) : offer.kind === "donation" ? (
                      <>
                        <p className="text-3xl font-semibold">You choose</p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          Suggested ${whole(offer.suggestedCents)} one-time
                          donation. Membership itself stays free.
                        </p>
                      </>
                    ) : (
                      (() => {
                        const count = quantity ?? offer.defaultQuantity;
                        // One fee per transaction, whatever the count — which
                        // is why the total is not a multiple of the price.
                        const total =
                          offer.membershipCents * count + offer.processingFeeCents;
                        return (
                          <>
                            <p className="text-3xl font-semibold">
                              ${whole(offer.membershipCents)}
                              <span className="text-muted-foreground text-base font-normal">
                                /year each
                              </span>
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                              <label
                                htmlFor="membership-count"
                                className="text-muted-foreground text-xs"
                              >
                                Memberships
                              </label>
                              <input
                                id="membership-count"
                                type="number"
                                min={1}
                                max={offer.maxQuantity}
                                value={count}
                                disabled={busy !== null}
                                onChange={(e) =>
                                  setQuantity(
                                    Math.min(
                                      offer.maxQuantity,
                                      Math.max(1, Number(e.target.value) || 1)
                                    )
                                  )
                                }
                                className="border-input h-8 w-16 rounded-md border px-2 text-sm"
                              />
                            </div>
                            {count > 1 && (
                              <p className="text-muted-foreground mt-2 text-xs">
                                {count - 1} sponsored{" "}
                                {count - 1 === 1 ? "membership" : "memberships"}{" "}
                                for members who need support.
                              </p>
                            )}
                            <p className="text-muted-foreground mt-2 text-xs">
                              ${whole(offer.membershipCents)}
                              {count > 1 ? ` × ${count}` : ""} + $
                              {exact(offer.processingFeeCents)} service fee
                              {count > 1 ? " (charged once)" : ""} = $
                              {exact(total)} total. All funds support AFELL
                              administration costs.
                            </p>
                          </>
                        );
                      })()
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button
                      variant={offer.recommended ? "default" : "outline"}
                      className="w-full"
                      disabled={busy !== null}
                      onClick={() => choose(offer)}
                    >
                      {busy === key && <Spinner data-slot="icon" />}
                      {busy === key
                        ? offer.kind === "free"
                          ? "Activating…"
                          : "Redirecting…"
                        : copy.cta}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {hasPaidOffer && info.addons.donation ? (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base">
                  Add to your membership
                </CardTitle>
                <CardDescription>
                  Billed once, together with your membership. Optional.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {info.addons.donation && (
                  <label className="hover:bg-accent/50 flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm">
                    <input
                      type="checkbox"
                      checked={donate}
                      onChange={(e) => setDonate(e.target.checked)}
                      disabled={busy !== null}
                      className="accent-primary mt-0.5 size-4 shrink-0"
                    />
                    <span>
                      <span className="font-medium">
                        Make a tax-deductible donation
                      </span>{" "}
                      You choose the amount at checkout. Supports AFELL
                      administration costs.
                    </span>
                  </label>
                )}
              </CardContent>
            </Card>
          ) : null}

          {failure && (
            <p className="text-destructive mt-4 text-sm">{failure.message}</p>
          )}
        </>
      )}
    </div>
  );
}
